import classNames from "classnames";
import debounce from "lodash/debounce";
import noop from "lodash/noop";
import { FunctionComponent, ReactNode, useCallback, useState } from "react";
import "./style.scss";

interface Props {
  children?: ReactNode;
  disabled?: boolean;
  expand?: boolean;
  href?: string;
  icon?: JSX.Element;
  iconPosition?: "left" | "alone" | "right";
  onClick?: (event: MouseEvent) => void;
  size: "xs" | "s" | "m";
  type: "primary" | "secondary" | "tertiary";
}

const ButtonSmily: FunctionComponent<Props> = ({
  children,
  disabled = false,
  expand = false,
  href,
  icon,
  iconPosition,
  onClick = noop,
  size,
  type,
}) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [neverExpand, setNeverExpand] = useState<boolean>(true); // To avoid animation at the page load
  const className = classNames(`ButtonSmily ButtonSmily--size-${size} ButtonSmily--type-${type}`, {
    "ButtonSmily--canExpand": expand,
    "ButtonSmily--disabled": disabled,
    "ButtonSmily--expanded": expand && expanded,
    "ButtonSmily--icon-alone": iconPosition === "alone",
    "ButtonSmily--icon-left": iconPosition === "left",
    "ButtonSmily--icon-right": iconPosition === "right",
    "ButtonSmily--reduced": expand && !expanded && !neverExpand,
  });

  // To avoid flickering effect if the state change too fast (400ms is the time of expand animation)
  const delayExpand = useCallback(
    debounce((state) => setExpanded(state), 400, { leading: true }),
    []
  );

  const handleMouseEnter = () => {
    setNeverExpand(false);
    delayExpand(true);
  };

  const handleMouseLeave = () => {
    delayExpand(false);
  };

  return href ? (
    <a
      className={className}
      href={href}
      onClick={onClick}
      onMouseEnter={expand ? handleMouseEnter : noop}
      onMouseLeave={expand ? handleMouseLeave : noop}
    >
      {!!icon && iconPosition === "left" && icon}
      {expand ? (
        <span className="ButtonSmily-expandedText">{!!children && children}</span>
      ) : (
        !!children && children
      )}
      {!!icon && (iconPosition === "alone" || iconPosition === "right") && icon}
    </a>
  ) : (
    <button
      className={className}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={expand ? handleMouseEnter : noop}
      onMouseLeave={expand ? handleMouseLeave : noop}
    >
      {!!icon && iconPosition === "left" && icon}
      {expand ? (
        <span className="ButtonSmily-expandedText">{!!children && children}</span>
      ) : (
        !!children && children
      )}
      {!!icon && (iconPosition === "alone" || iconPosition === "right") && icon}
    </button>
  );
};

export default ButtonSmily;
